"use client";

import { useState, useEffect, useRef } from "react";
import { useContractorConversationList } from "./useContractorConversationList.hooks";
import { useConversationList } from "./useConversationList.hooks";
import { useChatSocket } from "./useChatSocket.hooks";
import useHeader from "@/hooks/useHeader";
import { usePathname } from "next/navigation";

/**
 * Global hook to track if there are any unread messages for the contractor.
 * This hook handles the logic of showing a notification dot on the sidebar.
 * It distinguishes between 'Unread' (in the list) and 'Unseen' (on the sidebar tab).
 */
export const useChatNotification = () => {
  const { isLoggedIn, userType } = useHeader();
  const pathname = usePathname();
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const prevUnreadCount = useRef(0);
  
  const isContractor = userType === "Contractor";

  // 1. Initialize global socket listener
  useChatSocket(isLoggedIn, null);

  // 2. Fetch the conversation list based on role
  const contractorList = useContractorConversationList(isLoggedIn && isContractor);
  const homeownerList = useConversationList(isLoggedIn && !isContractor);

  const data = isContractor ? contractorList.data : homeownerList.data;

  // 3. Logic: Should we show the sidebar dot?
  const unreadCount = data?.filter((c) => c.isUnread).length ?? 0;

  useEffect(() => {
    const isMessagesPage = pathname.includes("/messages");

    // If we are currently on the messages page, we automatically acknowledge
    if (isMessagesPage) {
      setIsAcknowledged(true);
      return;
    }

    // If we have ZERO unreads, naturally the notification should be cleared
    if (unreadCount === 0) {
      setIsAcknowledged(true);
    } 
    // If we find NEW unread messages (count increased), we show the dot again
    else if (unreadCount > prevUnreadCount.current) {
      setIsAcknowledged(false);
    }
    
    prevUnreadCount.current = unreadCount;
  }, [unreadCount, pathname]);

  /**
   * Called when the user clicks the 'Messages' tab in the sidebar
   */
  const clearNotification = () => {
    setIsAcknowledged(true);
  };

  const hasUnread = unreadCount > 0 && !isAcknowledged;

  return {
    hasUnread,
    unreadCount,
    clearNotification
  };
};
